import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { isValidElement, type ReactNode } from 'react';

interface AgentCodeBlockProps {
  children?: ReactNode;
}

/**
 * Recursively extracts the raw text content from a ReactMarkdown `<pre>`'s
 * nested `<code>` children, flattening element wrappers down to their strings.
 *
 * @param {ReactNode} node - The children passed to the `pre` renderer
 * @returns {string} The concatenated plain-text source of the code block
 */
function extractCodeText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractCodeText).join('');
  if (isValidElement(node)) {
    return extractCodeText((node.props as { children?: ReactNode }).children);
  }
  return '';
}

/**
 * Renders a fenced markdown code block with a hover-revealed copy button.
 *
 * Used as the `pre` override for the agent message ReactMarkdown renderer so
 * users can copy individual code snippets without selecting the whole response.
 *
 * @param {ReactNode} children - The `<code>` element React-Markdown nests inside `pre`
 * @returns {JSX.Element} The styled code block with its copy affordance
 */
export function AgentCodeBlock({ children }: AgentCodeBlockProps) {
  const clipboard = useClipboard({ timeout: 1500 });
  const code = extractCodeText(children).replace(/\n$/, '');

  return (
    <CodeBlockWrap>
      <Tooltip label={clipboard.copied ? 'Copied!' : 'Copy'} position="left" withinPortal={false}>
        <CodeCopyBtn onClick={() => clipboard.copy(code)} type="button" aria-label="Copy code">
          <Icon
            icon={clipboard.copied ? 'material-symbols:check' : 'material-symbols:content-copy-outline'}
            width={14}
            height={14}
          />
        </CodeCopyBtn>
      </Tooltip>
      <pre>{children}</pre>
    </CodeBlockWrap>
  );
}

const CodeBlockWrap = styled.div`
  position: relative;
  margin: 8px 0;

  pre {
    background: var(--flora-pre-bg);
    color: var(--flora-pre-text);
    padding: 12px 14px;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 13.5px;
    margin: 0;

    code {
      background: none;
      padding: 0;
      color: inherit;
    }
  }

  &:hover button,
  &:focus-within button {
    opacity: 1;
  }
`;

const CodeCopyBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--flora-code-bg);
  border: 1px solid var(--flora-hover-subtle);
  cursor: pointer;
  padding: 4px;
  color: var(--flora-pre-text);
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s, background 0.15s;

  &:hover {
    color: var(--ink-base);
    background: var(--flora-hover-subtle);
  }
`;
